/**
 * Options for rendering a cents amount.
 */
export interface FormatCentsOptions {
  /** ISO 4217 code. Defaults to 'BRL'. */
  currency?: string;
  /** BCP-47 tag. Defaults to 'pt-BR'. */
  locale?: string;
  /** 'exact' (default) keeps centavos; 'whole' drops them for headline figures. */
  precision?: 'exact' | 'whole';
}

const DEFAULTS = { currency: 'BRL', locale: 'pt-BR', precision: 'exact' } as const;

/**
 * Formats an amount in cents as a localized currency string.
 *
 * Returns '' for null and undefined rather than 'R$ 0,00': a price the
 * provider did not report is absent, and rendering it as free is a different
 * claim. Zero itself is a real price and formats normally.
 *
 * @param cents - The amount in cents, or null when not reported.
 * @param options - Currency, locale and precision overrides.
 * @returns The formatted amount, or an empty string.
 */
export function formatCents(
  cents: number | null | undefined,
  options: FormatCentsOptions = {},
): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return '';
  const { currency, locale, precision } = { ...DEFAULTS, ...options };
  const digits = precision === 'whole' ? 0 : 2;
  return (cents / 100).toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}
